import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Loading } from '@/components/Loading';
import { Text } from '@/components/Text';
import { useAuthStore } from '@/stores/useAuthStore';
import { useEventStore } from '@/stores/useEventStore';
import { cn } from '@/utils/cn';
import { formatDateTime } from '@/utils/formatDateTime';
import { t } from 'i18next';
import { BsEnvelope, BsPeople, BsPerson, BsTelephone } from 'react-icons/bs';
import { FaMapMarkerAlt } from 'react-icons/fa';

export const EventDetailsInformations = () => {
  const { event } = useEventStore();
  const { user } = useAuthStore();
  const { addEventUser, removeEventUser } = useEventStore();

  if (!event) {
    return <Loading />;
  }

  const handleSubscribe = () => {
    if (event.user_is_subscribed) {
      removeEventUser(event.id, user?.id as number);
    } else {
      addEventUser(event.id, user?.id as number);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          <Badge
            label={t(`common:type.${event.type}`)}
            color={event.type === 'public' ? 'green' : 'orange'}
          />
          <Badge
            label={t(`common:status.${event.status}`)}
            color={
              event.status === 'future'
                ? 'blue'
                : event.status === 'past'
                  ? 'red'
                  : 'green'
            }
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Text className="font-medium text-gray-700">
              {t('common:fields.start_date')}
            </Text>
            <Text>{formatDateTime(event.date)}</Text>
          </div>
          <div className="space-y-2">
            <Text className="font-medium text-gray-700">
              {t('common:fields.end_date')}
            </Text>
            <Text>{formatDateTime(event.end_date)}</Text>
          </div>

          <div className="space-y-2">
            <Text className="font-medium text-gray-700">
              {t('eventDetails:participants')}
            </Text>
            <div className="flex items-center gap-2">
              <BsPeople className="size-5 text-gray-500" />
              {event.show_capacity ? (
                <>
                  <Text>{event.total_users}</Text>
                  {event.maximum_participants && (
                    <>
                      <span className="text-gray-400">/</span>
                      <Text>{event.maximum_participants}</Text>
                    </>
                  )}
                </>
              ) : (
                <Text>{event.total_users}</Text>
              )}
            </div>
          </div>

          {event.location && (
            <div className="space-y-2">
              <Text className="font-medium text-gray-700">
                {t('common:fields.location')}
              </Text>
              <div className="flex items-center gap-2">
                <FaMapMarkerAlt className="size-5 text-gray-500" />
                <Text>{event.location}</Text>
              </div>
            </div>
          )}

          {event.organizer && (
            <div className="space-y-2">
              <Text className="font-medium text-gray-700">
                {t('common:fields.organizer')}
              </Text>
              <div className="flex items-center gap-2">
                <BsPerson className="size-5 text-gray-500" />
                <Text>{event.organizer}</Text>
              </div>
            </div>
          )}

          {event.email && (
            <div className="space-y-2">
              <Text className="font-medium text-gray-700">
                {t('common:fields.email')}
              </Text>
              <div className="flex items-center gap-2">
                <BsEnvelope className="size-5 text-gray-500" />
                <Text>{event.email}</Text>
              </div>
            </div>
          )}

          {event.phone && (
            <div className="space-y-2">
              <Text className="font-medium text-gray-700">
                {t('common:fields.phone')}
              </Text>
              <div className="flex items-center gap-2">
                <BsTelephone className="size-5 text-gray-500" />
                <Text>{event.phone}</Text>
              </div>
            </div>
          )}
        </div>

        {event.description && (
          <div className="space-y-2">
            <Text className="font-medium text-gray-700">
              {t('common:fields.description')}
            </Text>
            <Text className="whitespace-pre-wrap">{event.description}</Text>
          </div>
        )}
      </div>
      <Button
        className={cn(
          'mt-4 block md:hidden',
          event.user_is_subscribed && 'bg-red-500',
          !event.user_is_subscribed && 'bg-green-500',
        )}
        onClick={handleSubscribe}
        disabled={event.status === 'ongoing' || event.status === 'past'}
      >
        {event.user_is_subscribed
          ? t('eventDetails:unsubscribe.button')
          : t('eventDetails:subscribe.button')}
      </Button>
    </>
  );
};
